/* eslint-disable react/no-unescaped-entities */

/**
 * ExpertCarousel — Carrousel 3D premium des spécialistes identifiés par CONFILEX MATCH™.
 *
 * Conformité déontologique stricte :
 *  - JAMAIS de "taux de succès", "meilleur spécialiste", classement perf
 *  - Score affiché = adéquation dossier uniquement
 *  - Mentions obligatoires CNB en pied de section
 *
 * UX :
 *  - Carte centrale dominante, 2 cartes latérales inclinées (perspective 3D)
 *  - Auto-rotation 4s, pause au hover/focus
 *  - Swipe tactile mobile + navigation flèches clavier
 *  - Modal "Comprendre ce score" (breakdown des 6 critères)
 *  - Modal vidéo (lecture sans quitter la page)
 *  - Boutons Consulter / Contacter / Voir vidéo (libre choix utilisateur)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, MapPin, Languages, ShieldCheck, Play, Info,
  CheckCircle2, Clock, X,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import PremiumPortraitFrame from "@/components/PremiumPortraitFrame";

const AUTO_ROTATE_MS = 4000;
const BREAKDOWN_LABELS = {
  matiere: "Adéquation discipline",
  specialisation: "Spécialisation officielle",
  competences: "Compétences détectées",
  experience: "Expérience déclarée",
  disponibilite: "Disponibilité",
  localisation: "Localisation",
  langues_bonus: "Langues (bonus)",
};

function initials(e) {
  return `${(e?.first_name || "").charAt(0)}${(e?.last_name || "").charAt(0)}`.toUpperCase() || "E";
}

export default function ExpertCarousel({
  experts = [],
  deontologyNotices = [],
  transparencyNotice = "",
  geoExpanded = false,
  matchId = null,
  warning = null,
}) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(null);
  const [videoOpen, setVideoOpen] = useState(null);
  const wrapperRef = useRef(null);
  const touchStart = useRef(null);

  const N = experts.length;
  const safeMod = useCallback((n) => ((n % N) + N) % N, [N]);
  const next = useCallback(() => setIdx((i) => safeMod(i + 1)), [safeMod]);
  const prev = useCallback(() => setIdx((i) => safeMod(i - 1)), [safeMod]);

  // Auto-rotation — pausée si une modal est ouverte ou si l'utilisateur interagit
  useEffect(() => {
    if (paused || breakdownOpen || videoOpen || N < 2) return;
    const t = setInterval(next, AUTO_ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, breakdownOpen, videoOpen, N, next]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [next, prev]);

  // Mobile swipe
  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
    setPaused(true);  // une interaction tactile met l'auto-rotation en pause
  };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    touchStart.current = null;
  };

  const visibleSlots = useMemo(() => {
    if (N === 0) return [];
    return [-1, 0, 1].map((offset) => ({ pos: offset, expert: experts[safeMod(idx + offset)] }));
  }, [idx, N, experts, safeMod]);

  if (N === 0) {
    return (
      <Card className="p-10 text-center bg-paper border-copper/30">
        <p className="text-sm text-night/70">Aucun professionnel pertinent n'a été identifié pour ce dossier.</p>
      </Card>
    );
  }

  return (
    <section
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="expert-carousel"
      data-match-id={matchId || undefined}
      aria-roledescription="carousel"
      aria-label="Professionnels pertinents identifiés par Confilex Match"
    >
      {/* Bandeau transparence + warning éventuel */}
      <div className="mb-6 text-center">
        <p className="text-[10px] uppercase tracking-[0.32em] text-copper mb-2">Confilex Match™</p>
        <h2 className="font-serif italic text-3xl sm:text-4xl text-night">
          {N} professionnel{N > 1 ? "s" : ""} identifié{N > 1 ? "s" : ""} pour votre dossier
        </h2>
        {geoExpanded && (
          <p className="text-[11px] text-copper-deep italic mt-2" data-testid="geo-expanded-notice">
            Périmètre géographique élargi — la compétence prime sur la distance.
          </p>
        )}
        {warning && (
          <p className="text-[12px] text-bordeaux italic mt-2" data-testid="match-warning">{warning}</p>
        )}
      </div>

      {/* Carrousel 3D */}
      <div
        className="relative h-[440px] sm:h-[480px] flex items-center justify-center"
        style={{ perspective: "1400px" }}
      >
        {visibleSlots.map(({ pos, expert }) => {
          const isCenter = pos === 0;
          const translateX = pos * (isCenter ? 0 : 320);
          const rotateY = pos * -22;
          const scale = isCenter ? 1 : 0.78;
          const zIndex = isCenter ? 20 : 10;
          const opacity = isCenter ? 1 : 0.6;
          return (
            <div
              key={`${pos}-${expert.id}`}
              className="absolute transition-all duration-700 ease-out will-change-transform"
              style={{
                transform: `translateX(${translateX}px) rotateY(${rotateY}deg) scale(${scale})`,
                zIndex,
                opacity,
              }}
              aria-hidden={!isCenter}
              data-testid={isCenter ? "carousel-center" : `carousel-side-${pos}`}
            >
              <ExpertCard
                expert={expert}
                emphasized={isCenter}
                onShowBreakdown={() => setBreakdownOpen(expert)}
                onShowVideo={() => setVideoOpen(expert)}
              />
            </div>
          );
        })}

        {/* Navigation manuelle */}
        {N > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Profil précédent"
              data-testid="carousel-prev"
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-ivory border border-sage hover:border-copper flex items-center justify-center shadow-sm transition"
            >
              <ChevronLeft className="w-5 h-5 text-night" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Profil suivant"
              data-testid="carousel-next"
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-ivory border border-sage hover:border-copper flex items-center justify-center shadow-sm transition"
            >
              <ChevronRight className="w-5 h-5 text-night" />
            </button>
          </>
        )}
      </div>

      {/* Indicateur de position */}
      <div className="flex justify-center gap-1.5 mt-4" data-testid="carousel-indicators">
        {experts.map((e, i) => (
          <button
            key={e.id}
            type="button"
            onClick={() => setIdx(i)}
            data-testid={`carousel-dot-${i}`}
            aria-label={`Aller au profil ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-copper" : "w-1.5 bg-sage hover:bg-night/40"}`}
          />
        ))}
      </div>

      {/* Mentions obligatoires CNB */}
      <Card className="mt-8 p-4 bg-paper border-copper/30">
        <p className="text-[11px] text-night/75 italic leading-relaxed mb-2">{transparencyNotice}</p>
        <ul className="space-y-1 text-[11px] text-night/65" data-testid="deontology-notices">
          {deontologyNotices.map((n, i) => (
            <li key={i} className="flex gap-1.5">
              <span className="text-copper">·</span><span>{n}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* Modal breakdown score */}
      {breakdownOpen && (
        <BreakdownModal expert={breakdownOpen} onClose={() => setBreakdownOpen(null)} />
      )}
      {/* Modal vidéo */}
      {videoOpen && (
        <VideoModal expert={videoOpen} onClose={() => setVideoOpen(null)} />
      )}
    </section>
  );
}

function ExpertCard({ expert, emphasized, onShowBreakdown, onShowVideo }) {
  const e = expert;
  const score = Math.round(e.adequation_score || 0);
  const photo = e.photo;
  const indicators = e.factual_indicators || {};

  return (
    <Card
      className={`w-72 sm:w-80 bg-ivory rounded-lg overflow-hidden flex flex-col ${
        emphasized
          ? "border-2 border-copper shadow-[0_20px_60px_-15px_rgba(196,164,104,0.45)]"
          : "border border-sage shadow-md"
      }`}
      data-testid={`expert-card-${e.id}`}
    >
      {/* Photo / Avatar — encadré "tableau de maître" pour les spécialistes référencés */}
      <PremiumPortraitFrame verified={!!e.verified} className="m-3">
        <div
          className="relative h-44 sm:h-52 overflow-hidden"
          style={{ background: "linear-gradient(135deg, var(--confilex-night), var(--confilex-petrol))" }}
        >
          {photo ? (
            <img src={photo} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-copper/20 border-2 border-copper flex items-center justify-center">
                <span className="font-serif text-3xl text-ivory">{initials(e)}</span>
              </div>
            </div>
          )}
          {/* Badge score d'adéquation */}
          <div
            className="absolute top-3 right-3 px-2 py-1 rounded-sm bg-ivory/95 backdrop-blur border border-copper text-night"
            data-testid={`adequation-badge-${e.id}`}
          >
            <p className="text-[8px] uppercase tracking-[0.18em] text-copper-deep leading-none mb-0.5">Adéquation dossier</p>
            <p className="font-serif italic text-lg leading-none">{score}<span className="text-xs">/100</span></p>
          </div>
          {/* Badge IEV (Indice d'Expérience Vérifiable) */}
          {typeof e.iev === "number" && (
            <div
              className="absolute top-3 left-3 px-2 py-1 rounded-sm bg-night/85 backdrop-blur text-ivory"
              data-testid={`iev-badge-${e.id}`}
              title={e.iev_label}
            >
              <p className="text-[8px] uppercase tracking-[0.18em] text-copper leading-none mb-0.5">IEV</p>
              <p className="font-serif italic text-lg leading-none">{e.iev}<span className="text-xs text-ivory/65">/100</span></p>
            </div>
          )}
          {e.video_url && (
            <button
              type="button"
              onClick={onShowVideo}
              data-testid={`video-btn-${e.id}`}
              className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-night/80 text-ivory text-[11px] backdrop-blur hover:bg-night transition"
            >
              <Play className="w-3 h-3" /> Voir la présentation vidéo
            </button>
          )}
        </div>
      </PremiumPortraitFrame>

      {/* Identité */}
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-[10px] uppercase tracking-[0.22em] text-copper">{e.profession}</p>
        <h3 className="font-serif italic text-xl text-night mt-1">
          {e.first_name} {e.last_name}
        </h3>
        {e.barreau && (
          <p className="text-[11px] text-night/65 mt-0.5">Barreau de {e.barreau}</p>
        )}

        {/* Métadonnées */}
        <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-night/70">
          {e.city && (<span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-copper" />{e.city}</span>)}
          {e.languages?.length > 0 && (<span className="inline-flex items-center gap-1"><Languages className="w-3 h-3 text-copper" />{e.languages.slice(0, 2).join(", ")}</span>)}
        </div>

        {/* Spécialités / Domaines (badges) */}
        {e.practice_areas?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {e.practice_areas.slice(0, 3).map((p, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-sm border border-sage bg-paper text-night/70">
                {p}
              </span>
            ))}
          </div>
        )}

        {/* Indicateurs factuels autorisés (P8) */}
        <div className="mt-3 space-y-1 text-[11px] text-night/65">
          {indicators.verified_by_confilex && (
            <p className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3 h-3 text-petrol" />Vérifié par Confilex</p>
          )}
          {indicators.average_response_hours && (
            <p className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3 text-petrol" />Réponse moyenne sous {indicators.average_response_hours}h</p>
          )}
          {typeof e.profile_completeness === "number" && e.profile_completeness >= 90 && (
            <p className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-petrol" />Profil complété à {e.profile_completeness}%</p>
          )}
        </div>

        {/* Actions */}
        <div className="mt-auto pt-4 flex gap-2 flex-wrap">
          <Button asChild className="rounded-sm bg-petrol text-ivory hover:bg-night text-xs flex-1" data-testid={`consult-${e.id}`}>
            <Link to={`/experts/${e.id}`}>Consulter ce profil</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-sm text-xs" data-testid={`contact-${e.id}`}>
            <Link to={`/missions/new?expert=${e.id}`}>Contacter</Link>
          </Button>
          <button
            type="button"
            onClick={onShowBreakdown}
            data-testid={`breakdown-${e.id}`}
            className="text-[11px] text-night/55 hover:text-copper inline-flex items-center gap-1 px-1"
            aria-label="Comprendre le score d'adéquation"
          >
            <Info className="w-3 h-3" /> Détails
          </button>
        </div>
      </div>
    </Card>
  );
}

function BreakdownModal({ expert, onClose }) {
  const e = expert;
  const b = e.adequation_breakdown || {};
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-night/60 backdrop-blur-sm p-4"
      data-testid="breakdown-modal"
      onClick={onClose}
    >
      <Card className="w-full max-w-md p-6 bg-ivory" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-copper">Score d'adéquation dossier</p>
            <h3 className="font-serif italic text-xl text-night">Comprendre le calcul</h3>
          </div>
          <button onClick={onClose} className="text-night/55 hover:text-night" aria-label="Fermer">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] text-night/70 italic mb-4 leading-relaxed">
          Ce score mesure uniquement la <b>correspondance objective</b> entre les caractéristiques
          de votre dossier et les informations professionnelles déclarées.
          Il ne mesure aucune performance, aucun taux de succès, aucun classement qualitatif.
        </p>
        <ul className="space-y-2">
          {Object.entries(b).map(([key, val]) => {
            if (!val || typeof val !== "object") return null;
            const pct = val.max > 0 ? Math.round((val.value / val.max) * 100) : 0;
            return (
              <li key={key} className="text-sm" data-testid={`breakdown-row-${key}`}>
                <div className="flex justify-between mb-1">
                  <span className="text-night/80">{BREAKDOWN_LABELS[key] || key}</span>
                  <span className="text-night/60 text-[11px]">{val.value}/{val.max}</span>
                </div>
                <div className="h-1.5 bg-sage/40 rounded-full overflow-hidden">
                  <div className="h-full bg-copper" style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
        <p className="text-[10px] text-night/55 italic mt-5 leading-relaxed">
          Algorithme CONFILEX MATCH™ — version tracée dans le journal d'audit.
        </p>
      </Card>
    </div>
  );
}

function VideoModal({ expert, onClose }) {
  const url = expert.video_url;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-night/80 backdrop-blur-sm p-4"
      data-testid="video-modal"
      onClick={onClose}
    >
      <div className="w-full max-w-2xl" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-ivory font-serif italic text-lg">
            Présentation — {expert.first_name} {expert.last_name}
          </p>
          <button onClick={onClose} className="text-ivory hover:text-copper" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="aspect-video bg-night rounded-sm overflow-hidden">
          {url ? (
            <video src={url} controls autoPlay className="w-full h-full" data-testid="video-player" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-ivory/60">Vidéo indisponible</div>
          )}
        </div>
      </div>
    </div>
  );
}
